import { useCallback, useMemo } from 'react';

import { type IInventoryItem,InventoryListingStatus } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';

import { useArchiveInventoryMutation } from '../api/useArchiveInventory';
import { getInventoryItem } from '../api/useGetInventoryItem';

/**
 * Categorized inventory items for archive operation
 */
export interface CategorizedArchiveItems {
	/** Items ready to archive */
	ready: IInventoryItem[];
	/** Items already archived */
	alreadyArchived: IInventoryItem[];
}

/**
 * Categorize items into ready and alreadyArchived groups
 */
const categorizeArchiveItems = (items: IInventoryItem[]): CategorizedArchiveItems => {
	const result: CategorizedArchiveItems = {
		ready: [],
		alreadyArchived: [],
	};

	for (const item of items) {
		// Check if already archived
		if (item.listing?.status === InventoryListingStatus.ARCHIVED) {
			result.alreadyArchived.push(item);
		} else {
			result.ready.push(item);
		}
	}

	return result;
};

/**
 * Hook for managing archive operations on selected inventory items
 * Provides execution using query hooks
 */
export const useArchiveInventorySelection = () => {
	const archiveMutation = useArchiveInventoryMutation();
	const currentServicesProviderId = useCurrentServicesProviderId();

	/**
	 * Execute archive for items by their IDs
	 * Fetches items using query hooks, categorizes, and archives ready items
	 * Returns the count of items that were archived
	 */
	const archiveItemsByIds = useCallback(
		async (itemIds: Set<string>): Promise<number> => {
			if (!currentServicesProviderId || itemIds.size === 0) {
				return 0;
			}

			// Fetch all items using query hooks
			const itemQueries = await Promise.all(
				Array.from(itemIds).map((id) =>
					getInventoryItem(currentServicesProviderId, id).catch((error) => {
						console.error(`Failed to fetch item ${id}:`, error);
						return null;
					}),
				),
			);

			// Filter out failed fetches
			const items = itemQueries.filter((item): item is IInventoryItem => item !== null);

			// Categorize items
			const categorized = categorizeArchiveItems(items);
			const readyItems = categorized.ready;

			if (readyItems.length === 0) {
				return 0;
			}

			const inventoryIds = readyItems.map((item) => item.id);

			await archiveMutation.mutateAsync({
				servicesProviderId: currentServicesProviderId,
				inventoryIds,
				archiveReason: 'manual admin portal',
			});

			return readyItems.length;
		},
		[archiveMutation, currentServicesProviderId],
	);

	return useMemo(
		() => ({
			archiveItemsByIds,
			isPending: archiveMutation.isPending,
		}),
		[archiveItemsByIds, archiveMutation.isPending],
	);
};
